import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  LivePost, LiveMediaItem, ReactionCounts, ReactionKey,
  REACTION_KEYS, REACTION_EMOJI, REACTION_LABEL, UPLOADS_BASE,
  buildStreamIframeUrl
} from '../../services/liveService';
import LivePhotoGrid from './LivePhotoGrid';

interface Props {
  post: LivePost;
  index: number;
  reactions?: ReactionCounts;
  myReaction?: ReactionKey | null;
  isAuthenticated?: boolean;
  onReact?: (postId: string, emoji: ReactionKey) => void;
}

const buildInitials = (s?: string) =>
  (s || '').trim().split(/\s+/).map(p => p[0] || '').slice(0, 2).join('').toUpperCase() || '·';

const formatTime = (iso: string) => {
  try {
    const d = new Date(iso);
    const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
    if (diffMin < 1) return 'ora';
    if (diffMin < 60) return `${diffMin} min fa`;
    return d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
};

const mediaSrc = (url: string) => (/^https?:\/\//.test(url) ? url : `${UPLOADS_BASE}${url}`);

const TYPE_PILL: Record<string, { label: string; cls: string }> = {
  text:    { label: 'testo',    cls: '' },
  photo:   { label: 'foto',     cls: 'foto' },
  video:   { label: 'video',    cls: 'video' },
  youtube: { label: 'video',    cls: 'video' },
  link:    { label: 'link',     cls: 'link' },
  article: { label: 'articolo', cls: 'articolo' },
};

const LinkPreview: React.FC<{ post: LivePost }> = ({ post }) => {
  const meta = post.linkMeta || {};
  const href = post.linkUrl || '#';
  return (
    <a className="link-preview" href={href} target="_blank" rel="noopener noreferrer">
      <div className="ph">
        {meta.thumbUrl && <img src={meta.thumbUrl} alt="" />}
        {meta.platform && <span className="yt-badge">{meta.platform}</span>}
      </div>
      <div className="info">
        {meta.domain && <div className="domain">{meta.domain}</div>}
        <div className="ti">{meta.title || href}</div>
        {meta.description && <div className="de">{meta.description}</div>}
      </div>
    </a>
  );
};

const ArticlePreview: React.FC<{ post: LivePost }> = ({ post }) => {
  const meta = post.linkMeta || {};
  const href = post.linkUrl || '#';
  const cover = meta.thumbUrl ? mediaSrc(meta.thumbUrl) : '';
  return (
    <a className="article-preview" href={href} target="_blank" rel="noopener noreferrer">
      {cover && <img className="cover" src={cover} alt={meta.title || ''} />}
      <div className="meta">
        <div className="kicker">Articolo blog{meta.domain ? ` · ${meta.domain}` : ''}</div>
        <div className="ti">{meta.title || ''}</div>
        {meta.description && <div className="de">{meta.description}</div>}
      </div>
    </a>
  );
};

const LivePostCard: React.FC<Props> = ({
  post, index, reactions, myReaction, isAuthenticated = false, onReact
}) => {
  const idx = String(index + 1).padStart(2, '0');
  const rep = typeof post.author?.representativeId === 'object' ? post.author?.representativeId : null;
  const authorName = post.author?.name
    || (rep ? `${rep.nome || ''} ${rep.cognome || ''}`.trim() : '');
  const initials = buildInitials(authorName);

  const pill = TYPE_PILL[post.kind] || TYPE_PILL.text;

  const photoItems: LiveMediaItem[] = (post.mediaItems && post.mediaItems.length > 0)
    ? post.mediaItems
    : (post.kind === 'photo' && post.mediaUrl ? [{ url: post.mediaUrl, mimeType: '' }] : []);

  // Reactions picker: renderizzato in PORTAL su document.body con position:fixed
  // (il .post-card ha overflow:hidden che taglierebbe il picker absolute).
  // Apre su hover/click del bottone; chiude su click-outside o Escape.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerPos, setPickerPos] = useState<{ top: number; left: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);

  const openPicker = () => {
    if (!isAuthenticated || !wrapRef.current) return;
    const r = wrapRef.current.getBoundingClientRect();
    // Picker stimato ~330px (7 emoji × ~44px + padding). Centra sopra il bottone con clamp.
    const PICKER_W = 330;
    let left = r.left + r.width / 2;
    if (left + PICKER_W / 2 > window.innerWidth - 12) left = window.innerWidth - 12 - PICKER_W / 2;
    if (left - PICKER_W / 2 < 12) left = 12 + PICKER_W / 2;
    setPickerPos({ top: r.top - 8, left });
    setPickerOpen(true);
  };

  useEffect(() => {
    if (!pickerOpen) return;
    const clickAway = (e: MouseEvent) => {
      const t = e.target as Node;
      const inWrap = wrapRef.current?.contains(t);
      const inPicker = pickerRef.current?.contains(t);
      if (!inWrap && !inPicker) setPickerOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setPickerOpen(false); };
    document.addEventListener('mousedown', clickAway);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', clickAway);
      document.removeEventListener('keydown', onEsc);
    };
  }, [pickerOpen]);

  const handlePick = (k: ReactionKey) => {
    if (!isAuthenticated || !onReact) return;
    onReact(post._id, k);
    setPickerOpen(false);
  };

  const handleQuickReact = () => {
    if (!isAuthenticated || !onReact) return;
    // Click sull'icona principale: toggle off se gia\` reagito, altrimenti pollice
    onReact(post._id, myReaction || 'like');
  };

  const counts = reactions || {} as ReactionCounts;
  const totalRxns = REACTION_KEYS.reduce((s, k) => s + (counts[k] || 0), 0);
  const topRxns = REACTION_KEYS
    .filter(k => (counts[k] || 0) > 0)
    .sort((a, b) => (counts[b] || 0) - (counts[a] || 0))
    .slice(0, 3);

  return (
    <article className="post">
      <div className="spine-dot">{idx}</div>

      <div className="post-card">
        <header className="post-head">
          <div className="who">
            {rep?.foto ? (
              <div className="av" style={{ background: '#000', overflow: 'hidden' }}>
                <img
                  src={mediaSrc(rep.foto)}
                  alt={authorName}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                />
              </div>
            ) : (
              <div className="av">{initials}</div>
            )}
            <div className="name">
              {authorName || 'Redazione'}
              <small>{formatTime(post.createdAt)} · Content creator</small>
            </div>
          </div>
          <span className={`type-pill ${pill.cls}`}>{pill.label}</span>
        </header>

        {post.kind === 'text' && post.text && (
          <div
            className="post-body unasca-article"
            dangerouslySetInnerHTML={{ __html: post.text }}
          />
        )}

        {post.caption && post.kind !== 'text' && (
          <div className="post-body"><p>{post.caption}</p></div>
        )}

        {post.kind === 'photo' && photoItems.length > 0 && (
          <div className="attach is-image">
            <LivePhotoGrid items={photoItems} alt={post.caption || authorName} />
          </div>
        )}

        {post.kind === 'video' && post.streamUid && (
          <div className="attach is-image" style={{ background: '#000' }}>
            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
              {post.streamStatus === 'ready' && buildStreamIframeUrl(post.streamUid) ? (
                <iframe
                  title="Video"
                  src={buildStreamIframeUrl(post.streamUid)}
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                  allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                  allowFullScreen
                />
              ) : post.streamStatus === 'error' ? (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 13 }}>
                  Video non disponibile
                </div>
              ) : (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 13 }}>
                  🔴 Video in arrivo — in elaborazione…
                </div>
              )}
            </div>
          </div>
        )}

        {post.kind === 'video' && post.mediaUrl && (
          <div className="attach is-image">
            <video src={mediaSrc(post.mediaUrl)} controls preload="metadata" />
          </div>
        )}

        {post.kind === 'youtube' && post.youtubeId && (
          <div className="attach is-image" style={{ background: '#000' }}>
            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
              <iframe
                title="YouTube"
                src={`https://www.youtube-nocookie.com/embed/${post.youtubeId}`}
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          </div>
        )}

        {post.kind === 'link' && <LinkPreview post={post} />}
        {post.kind === 'article' && <ArticlePreview post={post} />}

        <footer className="post-foot">
          <div className="reactions-summary">
            {topRxns.length > 0 && (
              <div className="stack">
                {topRxns.map(k => <span key={k}>{REACTION_EMOJI[k]}</span>)}
              </div>
            )}
            <span className="n">
              {totalRxns > 0 ? (
                <><strong>{totalRxns.toLocaleString('it-IT')}</strong> reazion{totalRxns === 1 ? 'e' : 'i'}</>
              ) : (
                <span style={{ opacity: 0.6 }}>0 reazioni</span>
              )}
            </span>
          </div>

          <div className="right-actions">
            <div ref={wrapRef} onMouseEnter={openPicker}>
              <button
                type="button"
                className={'action-btn' + (myReaction ? ' is-liked' : '')}
                onClick={handleQuickReact}
                disabled={!isAuthenticated}
                title={isAuthenticated ? (myReaction ? 'Rimuovi reazione' : 'Reagisci') : 'Accedi come associato per reagire'}
              >
                <span style={{ fontSize: 16 }}>
                  {myReaction ? REACTION_EMOJI[myReaction] : '👍'}
                </span>
                {myReaction ? REACTION_LABEL[myReaction] : 'Reagisci'}
              </button>
            </div>
          </div>
        </footer>
      </div>

      {/* Picker emoji renderizzato in portal su body con position:fixed per non essere
          tagliato dall'overflow:hidden del .post-card */}
      {pickerOpen && isAuthenticated && pickerPos && createPortal(
        <div
          ref={pickerRef}
          className="rxn-picker"
          style={{
            position: 'fixed',
            top: pickerPos.top,
            left: pickerPos.left,
            transform: 'translate(-50%, -100%)',
            zIndex: 9999,
            bottom: 'auto'
          }}
        >
          {REACTION_KEYS.map(k => (
            <button
              key={k}
              type="button"
              title={REACTION_LABEL[k]}
              onClick={() => handlePick(k)}
            >
              {REACTION_EMOJI[k]}
            </button>
          ))}
        </div>,
        document.body
      )}
    </article>
  );
};

export default LivePostCard;
